import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import 'rxjs/add/observable/fromEvent';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/startWith';
import 'rxjs/add/operator/debounceTime';
import 'rxjs/add/observable/merge';
import 'rxjs/add/operator/map';
import {DatatableComponent} from '@swimlane/ngx-datatable';
import {DataTableButton} from '../../interfaces/DataTableButton';
import {Subject} from 'rxjs/Subject';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {ValueAccessorBase} from '../../../../abstract/ValueAccessorBase';
import {Field} from '../../../form-builder/interfaces/Field';

export interface DataGridColumn<T> {
  prop: keyof T;
  editable?: boolean;
  name: string;
  type?: string;
  action?: (row: T) => void;
  filter?: FilterOptions,
  sortable?: boolean;
  value?: any;
  map?: (value: any) => any;
  getValue?: (row: T) => string
}

export interface FilterOptions {
  type: string;
  options?: { value: number, label: string }[];
}

@Component({
  selector: 'app-data-grid',
  templateUrl: './data-grid.component.html',
  styleUrls: ['./data-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: DataGridComponent, multi: true}
  ]
})
export class DataGridComponent<T> extends ValueAccessorBase<any[]> implements OnInit {
  @Input() loading = false;
  /**
   * Reference to the DataTable
   */
  @ViewChild(DatatableComponent) table: DatatableComponent;

  @Input() count: number;
  @Input() externalPaging: boolean;
  @Input() pagination: boolean;
  @Input() defaultSort: string;
  @Input() sortType = 'multi';
  @Input() columnMode = 'force';
  @Input() columnMinWidth = 100;
  @Input() wrapHeader = false;
  @Input() headerHeight = 50;
  @Input() reorderProp = 'order';
  /**
   * Column titles
   */
  @Input() columns: DataGridColumn<T>[];
  /**
   * Boolean value to determine if the columns can be sorted
   */
  @Input() sortable: boolean;
  /**
   * Boolean value to determine of the rows can be dragged
   */
  @Input() draggable: boolean;
  /**
   * Boolean value to determine if the row items can be edited
   */
  @Input() buttons: DataTableButton[] = [];

  @Input() from;
  @Input() to;
  @Input() limit = undefined;
  @Input() page = 1;
  @Input() filterable: boolean;
  /**
   * Function passed in to assign classes to particular cells
   */
  @Input() getCellClass: (rowData) => string;
  /**
   * Event emitted when a row item has been deleted
   * @type {EventEmitter}
   */
  @Output() onRemoved = new EventEmitter();
  /**
   * Event emitted when a row item has been edited
   * @type {EventEmitter}
   */
  @Output() onEdited = new EventEmitter();
  @Output() onLimitChanged = new EventEmitter();
  @Output() onPageChanged = new EventEmitter();
  @Output() onRowClicked = new EventEmitter();
  @Output() onFinishedReordering = new EventEmitter();
  @Output() onSorted = new EventEmitter();
  @Output() onFiltered = new EventEmitter();
  click$ = new Subject();
  /**
   * Internal state object for the table
   * @type Object
   */
  state = {
    limitOptions: [
      {key: '5', value: 5},
      {key: '10', value: 10},
      {key: '20', value: 20}
    ]
  };
  filters = [];
  editing = {};
  roleName: string;

  constructor(public elementRef: ElementRef) {
    super();
  }

  ngOnInit() {
    if (this.pagination) {
      // set initial row count per page limit
      if (!this.limit)
        this.limit = 10;
    }
    this.click$.debounceTime(1500).subscribe(() => {
      this.onFinishedReordering.emit(this.value);
    });

  }

  shouldDisplayButton(button: DataTableButton, row) {

    if (!button.condition) {
      return true;
    }
    return button.condition(row);
  }

  /**
   * Showing the tooltip base on user role name and if the status is Pending Approval.
   * @param {DataTableButton} button
   * @param row
   * @returns {any}
   */
  getTooltip(button: DataTableButton, row) {
    if (typeof button.tooltip === 'string')
      return button.tooltip;
    else if (typeof button.tooltip === 'function')
      return button.tooltip(row);
    return '';
  }

  buttonClicked(event: MouseEvent, button: DataTableButton, row, value) {
    if (button.action) {
      event.preventDefault();
      button.action(row, value);
    }

  }

  /**
   * Handles changing page
   * @param pageInfo
   */
  changePage(pageInfo) {
    this.page = pageInfo.offset + 1;
    this.onPageChanged.emit(this.page);
  }

  /**
   * Handles changing the row count per page
   * @param event
   */
  changeLimit(event) {
    this.limit = event;
    this.onLimitChanged.emit(event);
  }

  onActivate(event) {
    if (event.type === 'click') {
      this.onRowClicked.emit(event.row);
    }
  }

  reorderUp(id) {
    const reorderedItem = this.value.find(item => item.id === id);
    const otherReorderedItem = this.value.find((item, index) => index === reorderedItem[this.reorderProp] - 1);
    if (reorderedItem[this.reorderProp] === 0)
      return;
    otherReorderedItem[this.reorderProp] = otherReorderedItem[this.reorderProp] + 1;
    reorderedItem[this.reorderProp] = reorderedItem[this.reorderProp] - 1;
    this.reorder([reorderedItem, otherReorderedItem]);
    this.click$.next([reorderedItem, otherReorderedItem]);
  }

  reorderDown(id) {
    const reorderedItem = this.value.find(item => item.id === id);
    const otherReorderedItem = this.value.find((item, index) => index === reorderedItem[this.reorderProp] + 1);
    if (reorderedItem[this.reorderProp] >= this.value.length - 1)
      return;
    otherReorderedItem[this.reorderProp] = otherReorderedItem[this.reorderProp] - 1;
    reorderedItem[this.reorderProp] = reorderedItem[this.reorderProp] + 1;
    this.reorder([reorderedItem, otherReorderedItem]);
    this.click$.next([reorderedItem, otherReorderedItem]);
  }

  reorder(reorderedItems: Field[]) {
    reorderedItems.forEach(updatedField => {
      this.value = [
        ...this.value.slice(0, updatedField[this.reorderProp]),
        updatedField,
        ...this.value.slice(updatedField[this.reorderProp] + 1)
      ];
    });
  }

  canReorderUp(row) {
    return row[this.reorderProp] === 0;
  }

  canReorderDown(row) {
    return row[this.reorderProp] === this.value.length - 1;
  }

  onSort(sort) {
    this.onSorted.emit(sort);
  }

  onFilterChanged(filter) {
    this.filters = this.filters
      .filter(f => f.column !== filter.column)
      .concat(filter);
    this.onFiltered.emit(this.filters);
  }

  updateValue(event, cell, rowIndex) {
    this.editing[rowIndex + '-' + cell] = false;
    this.value[rowIndex][cell] = event.target.value;
    this.value = [...this.value];
  }

  onCellClick(row, column) {
    column.action(row);
  }

  getRowValue<T>(row: T, value: any, column: DataGridColumn<T>) {
    if (column.getValue)
      value = column.getValue(row);

    if (column.map)
      return column.map(value);
    return value;
  }

  getSortable(column) {
    if (column.sortable === undefined) return true;
    else return column.sortable
  }

}



// WEBPACK FOOTER //
// /var/www/dnata-cerf-front-end/releases/20200326161233/src/app/modules/utilities/components/data-grid/data-grid.component.ts